<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AgendaToolbar from './components/AgendaToolbar.vue';
import AgendaWeekView from './components/AgendaWeekView.vue';
import AgendaThreeDayView from './components/AgendaThreeDayView.vue';
import AgendaMonthView from './components/AgendaMonthView.vue';
import AgendaDayView from './components/AgendaDayView.vue';
import AgendaListView from './components/AgendaListView.vue';
import AgendaMeetingModal from './components/AgendaMeetingModal.vue';
import AgendaSettings from './components/AgendaSettings.vue';
import KanbanOpportunityModal from 'dashboard/modules/kanban/KanbanOpportunityModal.vue';
import { fromSpDatetimeLocal, spDateKey } from './agendaDateUtils';

const store = useStore();
const meetings = useMapGetter('agenda/allMeetings');
const userAgenda = useMapGetter('agenda/userAgenda');
const uiFlags = useMapGetter('agenda/uiFlags');
const currentUser = useMapGetter('getCurrentUser');

const currentAccountId = useMapGetter('getCurrentAccountId');

const currentUserId = currentUser.value?.id;
const AGENDA_VIEW_STORAGE_KEY = `agenda_active_view_${currentUserId}`;
// por conta: os filtros guardam ids de usuários, que não existem nas outras contas
const AGENDA_FILTERS_STORAGE_KEY = `agenda_filters_pref_${currentAccountId.value}_${currentUserId}`;
const VALID_VIEWS = ['week', 'threeDay', 'day', 'month', 'list'];

function loadStoredView() {
  const stored = localStorage.getItem(AGENDA_VIEW_STORAGE_KEY);
  return VALID_VIEWS.includes(stored) ? stored : 'week';
}

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(AGENDA_FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

const activeView = ref(loadStoredView());
const currentDate = ref(new Date());

const MOBILE_ALLOWED_VIEWS = ['day', 'list'];

watch(
  isMobile,
  mobile => {
    if (mobile) {
      const stored = loadStoredView();
      activeView.value = MOBILE_ALLOWED_VIEWS.includes(stored) ? stored : 'day';
    } else {
      activeView.value = loadStoredView();
    }
  },
  { immediate: true }
);

const activeFilters = ref(loadStoredFilters());
const selectedMeetingId = ref(null);
const showNewMeetingForm = ref(false);
const newMeetingSlot = ref(null);
const showSettings = ref(false);
const openedCard = ref(null);

const defaultDuration = computed(
  () => userAgenda.value?.default_duration ?? 60
);

function spMidnight(dateKey) {
  return fromSpDatetimeLocal(`${dateKey}T00:00`);
}

function spEndOfDay(dateKey) {
  return fromSpDatetimeLocal(`${dateKey}T23:59`);
}

function rangeParams() {
  const d = new Date(currentDate.value);
  let startKey;
  let endKey;

  if (activeView.value === 'week') {
    const dayOfWeek = d.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const start = new Date(d);
    start.setDate(d.getDate() + diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    startKey = spDateKey(start);
    endKey = spDateKey(end);
  } else if (activeView.value === 'threeDay') {
    const end = new Date(d);
    end.setDate(d.getDate() + 2);
    startKey = spDateKey(d);
    endKey = spDateKey(end);
  } else if (activeView.value === 'day') {
    startKey = spDateKey(d);
    endKey = startKey;
  } else if (activeView.value === 'month') {
    const year = d.getFullYear();
    const month = d.getMonth();
    startKey = spDateKey(new Date(year, month, 1));
    endKey = spDateKey(new Date(year, month + 1, 0));
  }

  return {
    start_from: spMidnight(startKey),
    start_to: spEndOfDay(endKey),
  };
}

function fetchMeetings() {
  if (activeView.value === 'list') return;
  store.dispatch('agenda/fetchMeetings', {
    ...rangeParams(),
    ...activeFilters.value,
  });
}

watch([activeView, currentDate], fetchMeetings);

watch(activeView, view => {
  if (!isMobile.value) {
    localStorage.setItem(AGENDA_VIEW_STORAGE_KEY, view);
  }
});

onMounted(async () => {
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('agenda/fetchAgenda'),
  ]);
  fetchMeetings();
});

function onFilterChange(filters) {
  activeFilters.value = filters;
  try {
    localStorage.setItem(AGENDA_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignora falha de persistência
  }
  fetchMeetings();
}

function onNewMeetingSlot(slot) {
  newMeetingSlot.value = slot;
  showNewMeetingForm.value = true;
  selectedMeetingId.value = null;
}

function onMeetingClick(id) {
  selectedMeetingId.value = id;
  showNewMeetingForm.value = false;
  newMeetingSlot.value = null;
}

function onModalClose() {
  selectedMeetingId.value = null;
  showNewMeetingForm.value = false;
  newMeetingSlot.value = null;
}

function onMeetingCreated() {
  fetchMeetings();
}

function onMeetingUpdated() {
  fetchMeetings();
}

function onMeetingDeleted() {
  fetchMeetings();
}

async function onMeetingDrop({ id, start_at, end_at, originalMeeting }) {
  store.commit('agenda/UPDATE_MEETING', {
    ...originalMeeting,
    start_at,
    end_at,
  });

  try {
    await store.dispatch('agenda/updateMeeting', { id, start_at, end_at });
  } catch {
    store.commit('agenda/UPDATE_MEETING', originalMeeting);
    useAlert('Não foi possível reagendar a reunião. Tente novamente.');
  }
}

const showModal = computed(
  () => selectedMeetingId.value !== null || showNewMeetingForm.value
);

async function onOpenCard(card) {
  await store.dispatch('kanban/fetchPipelines');
  openedCard.value = card;
}
</script>

<template>
  <div class="flex flex-col h-full w-full overflow-hidden">
    <AgendaToolbar
      :active-view="activeView"
      :current-date="currentDate"
      :active-filters="activeFilters"
      @update:active-view="activeView = $event"
      @update:current-date="currentDate = $event"
      @filter-change="onFilterChange"
      @new-meeting="
        showNewMeetingForm = true;
        selectedMeetingId = null;
        newMeetingSlot = null;
      "
    />

    <!-- Conteúdo da view -->
    <div class="flex flex-1 overflow-hidden relative">
      <!-- Loading overlay -->
      <div
        v-if="uiFlags.isLoading"
        class="absolute inset-0 bg-n-background/60 flex items-center justify-center z-20"
      >
        <span class="i-lucide-loader-2 size-6 text-n-brand animate-spin" />
      </div>

      <AgendaWeekView
        v-if="activeView === 'week'"
        :current-date="currentDate"
        :meetings="meetings"
        :default-duration="defaultDuration"
        @meeting-click="onMeetingClick"
        @new-meeting-slot="onNewMeetingSlot"
        @meeting-drop="onMeetingDrop"
      />

      <AgendaThreeDayView
        v-else-if="activeView === 'threeDay'"
        :current-date="currentDate"
        :meetings="meetings"
        :default-duration="defaultDuration"
        @meeting-click="onMeetingClick"
        @new-meeting-slot="onNewMeetingSlot"
        @meeting-drop="onMeetingDrop"
      />

      <AgendaDayView
        v-else-if="activeView === 'day'"
        :current-date="currentDate"
        :meetings="meetings"
        @meeting-click="onMeetingClick"
        @new-meeting-slot="onNewMeetingSlot"
        @meeting-drop="onMeetingDrop"
      />

      <AgendaMonthView
        v-else-if="activeView === 'month'"
        :current-date="currentDate"
        :meetings="meetings"
        @meeting-click="onMeetingClick"
        @new-meeting-slot="onNewMeetingSlot"
        @meeting-drop="onMeetingDrop"
      />

      <AgendaListView
        v-else-if="activeView === 'list'"
        :active-filters="activeFilters"
        @meeting-click="onMeetingClick"
      />
    </div>

    <!-- Modal de reunião -->
    <AgendaMeetingModal
      v-if="showModal"
      :meeting-id="selectedMeetingId"
      :default-slot="newMeetingSlot"
      @close="onModalClose"
      @created="onMeetingCreated"
      @updated="onMeetingUpdated"
      @deleted="onMeetingDeleted"
      @open-card="onOpenCard"
    />

    <!-- Settings -->
    <AgendaSettings v-if="showSettings" @close="showSettings = false" />

    <!-- Kanban card modal aberto a partir de uma reunião -->
    <KanbanOpportunityModal
      v-if="openedCard"
      :card="openedCard"
      @close="openedCard = null"
      @saved="openedCard = null"
      @deleted="openedCard = null"
    />

    <!-- Backdrop do modal -->
    <div
      v-if="showModal || showSettings"
      class="fixed inset-0 bg-black/20 z-40"
      @click="showSettings ? (showSettings = false) : onModalClose()"
    />
  </div>
</template>
